import React, { useEffect, useRef } from "react";
import { AccountType } from "../utility/enums";
import { auth } from "../firebase.config";
import UserSystemMessage from "./UserSystemMessage";
import GetFileTypeElement, { fileColors } from "../Data/Functions/getFileType";

function getInitials(name) {
    if (!name) return "";
    const words = name.trim().split(/\s+/);
    if (words.length === 0) return "";
    if (words.length > 1) return (words[0][0] + words[1][0]).toUpperCase();
    return (words[0][0] + (words[0][1] || "")).toUpperCase();
}

function formatFileSize(bytes) {
    if (!bytes) return "0 KB";
    const kb = bytes / 1024;
    if (kb < 1024) return `${kb.toFixed(1)} KB`;
    return `${(kb / 1024).toFixed(1)} MB`;
}

function formatTime(timestamp) {
    return new Date(
        timestamp?.seconds ? timestamp.seconds * 1000 : timestamp
    ).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
}

function formatDateHeader(timestamp) {
    const date = new Date(timestamp?.seconds ? timestamp.seconds * 1000 : timestamp);
    const today = new Date();
    const yesterday = new Date();
    yesterday.setDate(today.getDate() - 1);

    if (date.toDateString() === today.toDateString()) return "Today";
    if (date.toDateString() === yesterday.toDateString()) return "Yesterday";

    return date.toLocaleDateString([], {
        weekday: "long",
        day: "numeric",
        month: "short",
    });
}

function groupMessagesByDate(messages) {
    return messages.reduce((groups, msg) => {
        const date = new Date(msg.timestamp?.seconds ? msg.timestamp.seconds * 1000 : msg.timestamp);
        const key = date.toDateString();
        if (!groups[key]) groups[key] = [];
        groups[key].push(msg);
        return groups;
    }, {});
}

export default function ChatMessages({ messages, userRole, userData }) {
    const el = useRef(null);
    useEffect(() => {
        const timeout = setTimeout(() => {
            if (el.current) {
                el.current.scrollIntoView({ behavior: "smooth", block: "end" });
            }
        }, 100); // Ensures the DOM updates before scrolling
    
        return () => clearTimeout(timeout); // Cleanup function to prevent memory leaks
    }, [messages]);
    return (
        <div className="messages-container customScroll">
            {Object.entries(groupMessagesByDate(messages)).map(([dateKey, msgs]) => (
                <div key={dateKey} className="flex flex-col gap-4">
                    <div className="date-header">{formatDateHeader(msgs[0].timestamp)}</div>

                    {msgs.map((msg, index) => {
                        // Decide alignment (left/right) for each message
                        let wrapperClass = "";
                        if (msg.type === "SYSTEM_MSG") {
                            wrapperClass = userData.role === AccountType.CLIENT ? "received" : "sent";
                        } else if (msg.type === "USER_SYSTEM_MSG") {
                            wrapperClass = userData.role === AccountType.CLIENT ? "sent" : "received";
                        } else {
                            if (msg.senderId === userData.id) {
                                wrapperClass = "sent";
                            } else {
                                wrapperClass = "received";
                            }
                        }

                        // Decide message bubble style
                        let messageClass = "";
                        if (msg.type === "SYSTEM_MSG") {
                            messageClass =
                                userData.role === AccountType.CLIENT
                                    ? "received-message system-msg"
                                    : "sent-message system-msg";
                        } else if (msg.type === "USER_SYSTEM_MSG") {
                            messageClass =
                                userData.role === AccountType.CLIENT
                                    ? "sent-message user-system-msg"
                                    : "received-message user-system-msg";
                        } else {
                            messageClass =
                                msg.senderId === userData.id ? "sent-message" : "received-message";
                        }

                        // Should show avatar on left?
                        const showLeftAvatar =
                            (msg.type === "SYSTEM_MSG" && userData.role === AccountType.CLIENT) ||
                            (msg.type === "USER_SYSTEM_MSG" && userData.role !== AccountType.CLIENT) ||
                            (!msg.type &&
                                (!msg.type && msg.senderId !== userData.id));

                        // Should show avatar on right?
                        const showRightAvatar =
                            (msg.type === "SYSTEM_MSG" && userData.role !== AccountType.CLIENT) ||
                            (msg.type === "USER_SYSTEM_MSG" && userData.role === AccountType.CLIENT) ||
                            (!msg.type &&
                                (!msg.type && msg.senderId === userData.id));

                        return (
                            <div key={index} className={`message-wrapper ${wrapperClass}`}>
                                {/* LEFT AVATAR */}
                                {showLeftAvatar && (
                                    <div className="avatar-container">
                                        {msg.type === "SYSTEM_MSG" ? (
                                            <img
                                                src={require("../assets/Avatar for comment field.png")}
                                                alt="System"
                                                className="avatar system-avatar"
                                            />
                                        ) : msg.type === "USER_SYSTEM_MSG" ? (
                                            <div className="avatar">{getInitials(msg?.senderName || "")}</div>
                                        ) : msg?.profilePic?.url ? (
                                            <img src={msg.profilePic.url} alt={getInitials(msg?.senderName)} className="avatar" />
                                        ) : (
                                            <div className="avatar">{getInitials(msg?.senderName || "UN")}</div>
                                        )}
                                        <div className="tooltip left">{msg?.senderName}</div>
                                    </div>
                                )}

                                {msg.type === "USER_SYSTEM_MSG" ? (
                                    <UserSystemMessage msg={msg} userData={userData} />
                                ) : (
                                    <div className={`message ${messageClass}`}>
                                        <div
                                            className="chatMessages"
                                            dangerouslySetInnerHTML={{ __html: msg.message }}
                                        />
                                        {msg?.files?.length > 0 && (
                                            <div className="w-full flex flex-col justify-center items-start gap-2" >
                                                {msg.files.map((file, index) => (
                                                      <div className="fileUploaderLineInfo max-w-fit rounded-[4px] px-2" style={{gap:'2px', cursor: "pointer", backgroundColor: fileColors[file?.name?.split(".").pop().toLowerCase()]?.bg || "#FFD280"}} 
                                                      onClick={() => file?.url && window.open(file.url, "_blank")}>
                                                        <GetFileTypeElement file={file}/>
                                                        <p
                                                            className="fileUploaderLineName" style={{ maxWidth: "100%", color: fileColors[file?.name?.split(".").pop().toLowerCase()]?.color || "#FFD280" , fontSize: "12px"}}
                                                        >
                                                            {file.name}
                                                        </p>
                                                        </div>
                                                ))}
                                            </div>
                                        )}
                                        <span className="messageTime">
                                            {msg.timestamp ? formatTime(msg.timestamp) : ""}
                                        </span>
                                    </div>
                                )}

                                {/* RIGHT AVATAR */}
                                {showRightAvatar && (
                                    <div className="avatar-container">
                                        {msg.type === "SYSTEM_MSG" ? (
                                            <img
                                                src={require("../assets/Avatar for comment field.png")}
                                                alt="System"
                                                className="avatar system-avatar"
                                            />
                                        ) : msg.type === "USER_SYSTEM_MSG" ? (
                                            auth?.currentUser?.photoURL ? (
                                                <img
                                                    src={auth?.currentUser?.photoURL}
                                                    alt={getInitials(`${userData?.firstName || ""} ${userData?.lastName || ""}`)}
                                                    className="avatar"
                                                />
                                            ) : (
                                                <div className="avatar">{getInitials(msg?.senderName || "")}</div>
                                            )
                                        ) : msg?.profilePic?.url ? (
                                            <img src={msg.profilePic.url} alt={getInitials(msg?.senderName)} className="avatar" />
                                        ) : (
                                            <div className="avatar">{getInitials(msg?.senderName || "U")}</div>
                                        )}
                                        <div className="tooltip right">{msg?.senderName}</div>
                                    </div>
                                )}
                                
                            </div>
                        );
                        
                    })}
                </div>
            ))}

            <div id={"el"} ref={el} />
        </div>
    );
}